/**
 * Helpers for Intelligence purchases (capacity + credit packs) across payment methods.
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebaseSetUp';
import {
  buyIntelligenceCredits,
  createIntelligenceCheckout,
} from './paymentsServer';

const CAPTURE_STORAGE_PREFIX = 'ves-payment-captures';

export type IntelligencePayMethod = 'stripe' | 'ves' | 'binance';

async function uploadVesCapture(
  organizationId: string,
  captureFile: File
): Promise<{ captureUrl: string; storagePath: string }> {
  const timestamp = Date.now();
  const storagePath = `${CAPTURE_STORAGE_PREFIX}/${organizationId}/intel_${timestamp}_${captureFile.name}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, captureFile);
  const captureUrl = await getDownloadURL(storageRef);
  return { captureUrl, storagePath };
}

export async function purchaseIntelligenceCapacity(params: {
  organizationId: string;
  capacityId: 'lite' | 'plus' | 'extra';
  paymentMethod: IntelligencePayMethod;
  exchangeRate?: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureFile?: File;
  amountUSD?: number;
}) {
  const {
    organizationId,
    capacityId,
    paymentMethod,
    exchangeRate,
    exchangeSourceId,
    exchangeSourceName,
    captureFile,
    amountUSD,
  } = params;

  let vesExtras: {
    exchangeRate?: number;
    exchangeSourceId?: string;
    exchangeSourceName?: string;
    captureUrl?: string;
    storagePath?: string;
    amountUSD?: number;
    amountVES?: number;
  } = {};

  if (paymentMethod === 'ves') {
    if (!captureFile || !exchangeRate || exchangeRate <= 0) {
      throw new Error('Tasa de cambio y captura son requeridas para pagar en bolívares');
    }
    const uploaded = await uploadVesCapture(organizationId, captureFile);
    const usd = amountUSD ?? 0;
    vesExtras = {
      exchangeRate,
      exchangeSourceId,
      exchangeSourceName,
      captureUrl: uploaded.captureUrl,
      storagePath: uploaded.storagePath,
      amountUSD: usd > 0 ? usd : undefined,
      amountVES: usd > 0 ? Math.round(usd * exchangeRate) : undefined,
    };
  }

  return createIntelligenceCheckout({
    organizationId,
    capacityId,
    successUrl: window.location.href,
    cancelUrl: window.location.href,
    paymentMethod,
    ...vesExtras,
  });
}

export async function purchaseIntelligenceCreditPack(params: {
  organizationId: string;
  packId: string;
  quantity: number;
  paymentMethod: IntelligencePayMethod;
  exchangeRate?: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureFile?: File;
  amountUSD?: number;
}) {
  const {
    organizationId,
    packId,
    quantity,
    paymentMethod,
    exchangeRate,
    exchangeSourceId,
    exchangeSourceName,
    captureFile,
    amountUSD,
  } = params;

  let vesExtras: {
    exchangeRate?: number;
    exchangeSourceId?: string;
    exchangeSourceName?: string;
    captureUrl?: string;
    storagePath?: string;
    amountUSD?: number;
    amountVES?: number;
  } = {};

  if (paymentMethod === 'ves') {
    if (!captureFile || !exchangeRate || exchangeRate <= 0) {
      throw new Error('Tasa de cambio y captura son requeridas para pagar en bolívares');
    }
    const uploaded = await uploadVesCapture(organizationId, captureFile);
    const usd = amountUSD ?? 0;
    vesExtras = {
      exchangeRate,
      exchangeSourceId,
      exchangeSourceName,
      captureUrl: uploaded.captureUrl,
      storagePath: uploaded.storagePath,
      amountUSD: usd > 0 ? usd : undefined,
      amountVES: usd > 0 ? Math.round(usd * exchangeRate) : undefined,
    };
  }

  return buyIntelligenceCredits({
    organizationId,
    packId,
    quantity,
    paymentMethod,
    successUrl: window.location.href,
    cancelUrl: window.location.href,
    ...vesExtras,
  });
}
